import type { Client, ClientHeaders } from "@medusajs/js-sdk";

import type { SalonStockerHttpTypes } from "@salonstocker/framework";

/**
 * A saved list as it appears in the cheap index - counts only, no per-line
 * offer/price resolution (the index perf boundary). The full list with its lines
 * comes from {@link SavedList.retrieve}; live price/availability from
 * {@link SavedList.preview}.
 */
export interface StoreSavedListSummary {
  id: string;
  name: string;
  item_count: number;
  seller_count: number;
  last_used_at: string | null;
  created_by: SalonStockerHttpTypes.StoreSavedListActor;
}

export interface StoreSavedListsResponse {
  saved_lists: StoreSavedListSummary[];
  count?: number;
  offset?: number;
  limit?: number;
}

export interface StoreSavedListResponse {
  saved_list: SalonStockerHttpTypes.StoreSavedList;
}

export interface StoreSavedListDeleteResponse {
  id: string;
  object: string;
  deleted: boolean;
}

/**
 * Result of resolving a saved list against live offers. `items` is the
 * classified/priced diff (the review-dialog payload); `carts` is only present
 * on a real apply (`dry_run: false`) - the per-seller carts the lines landed in.
 */
export interface StoreAddSavedListToCartResponse {
  items: SalonStockerHttpTypes.StoreResolvedSavedListItem[];
  carts?: { seller_id: string; cart_id: string }[];
}

/** Optional pagination for the saved-list index. */
export interface StoreSavedListsListQuery {
  offset?: number;
  limit?: number;
}

/** A buyer's per-line pick + quantity override, keyed by `variant_id`. */
export interface StoreSavedListLineSelection {
  variant_id: string;
  quantity: number;
}

/**
 * Client for the buyer **Saved Lists** endpoints (`/store/saved-lists`, F3).
 *
 * A saved list is a cross-supplier routing manifest, not a cart: item ops are
 * keyed by the `(seller_id, variant_id)` pair, and {@link addToCart} fans the
 * list out into the per-seller carts (reusing the reorder machinery). Mirrors
 * the two-phase reorder client: {@link preview} resolves without mutating a
 * cart (drives the review dialog); {@link addToCart} applies.
 */
export class SavedList {
  private client: Client;

  constructor(client: Client) {
    this.client = client;
  }

  /** Cheap index of the salon's lists (counts only, no per-line resolve). */
  list = async (
    query?: StoreSavedListsListQuery,
    headers?: ClientHeaders
  ): Promise<StoreSavedListsResponse> =>
    this.client.fetch<StoreSavedListsResponse>("/store/saved-lists", {
      method: "GET",
      query,
      headers
    });

  /** Create a list, optionally seeded with lines. */
  create = async (
    body: SalonStockerHttpTypes.StoreCreateSavedList,
    headers?: ClientHeaders
  ): Promise<StoreSavedListResponse> =>
    this.client.fetch<StoreSavedListResponse>("/store/saved-lists", {
      method: "POST",
      body,
      headers
    });

  /** Fetch a single list with its stored lines (no live price). */
  retrieve = async (
    id: string,
    headers?: ClientHeaders
  ): Promise<StoreSavedListResponse> =>
    this.client.fetch<StoreSavedListResponse>(`/store/saved-lists/${id}`, {
      method: "GET",
      headers
    });

  /** Rename a list. */
  rename = async (
    id: string,
    name: string,
    headers?: ClientHeaders
  ): Promise<StoreSavedListResponse> =>
    this.client.fetch<StoreSavedListResponse>(`/store/saved-lists/${id}`, {
      method: "POST",
      body: { name } satisfies SalonStockerHttpTypes.StoreUpdateSavedList,
      headers
    });

  /** Delete a list. */
  delete = async (
    id: string,
    headers?: ClientHeaders
  ): Promise<StoreSavedListDeleteResponse> =>
    this.client.fetch<StoreSavedListDeleteResponse>(
      `/store/saved-lists/${id}`,
      {
        method: "DELETE",
        headers
      }
    );

  /**
   * Add a line, or overwrite the quantity of the matching
   * `(seller_id, variant_id)` line - never sum.
   */
  addItem = async (
    id: string,
    item: SalonStockerHttpTypes.StoreAddSavedListItem,
    headers?: ClientHeaders
  ): Promise<StoreSavedListResponse> =>
    this.client.fetch<StoreSavedListResponse>(
      `/store/saved-lists/${id}/items`,
      {
        method: "POST",
        body: item,
        headers
      }
    );

  /** Set the quantity of a line, identified by its `(seller_id, variant_id)` key. */
  updateItem = async (
    id: string,
    item: SalonStockerHttpTypes.StoreUpdateSavedListItem,
    headers?: ClientHeaders
  ): Promise<StoreSavedListResponse> =>
    this.client.fetch<StoreSavedListResponse>(
      `/store/saved-lists/${id}/items/update`,
      { method: "POST", body: item, headers }
    );

  /** Remove a line, identified by its `(seller_id, variant_id)` key. */
  removeItem = async (
    id: string,
    key: SalonStockerHttpTypes.StoreRemoveSavedListItem,
    headers?: ClientHeaders
  ): Promise<StoreSavedListResponse> =>
    this.client.fetch<StoreSavedListResponse>(
      `/store/saved-lists/${id}/items/remove`,
      { method: "POST", body: key, headers }
    );

  /**
   * Resolve the list against live offers WITHOUT touching any cart - the
   * dry-run that drives the list-detail view and the review-before-add dialog.
   */
  preview = async (
    id: string,
    headers?: ClientHeaders
  ): Promise<StoreAddSavedListToCartResponse> =>
    this.client.fetch<StoreAddSavedListToCartResponse>(
      `/store/saved-lists/${id}/add-to-cart`,
      { method: "POST", body: { dry_run: true }, headers }
    );

  /**
   * Apply the list: fan its available lines out into the per-seller carts
   * (overwrite-on-conflict, never sum) and return the affected carts.
   * `selections`, when given, narrows the add to a buyer-picked subset at
   * buyer-picked quantities instead of every addable line at its saved
   * quantity.
   */
  addToCart = async (
    id: string,
    selections?: StoreSavedListLineSelection[],
    headers?: ClientHeaders
  ): Promise<StoreAddSavedListToCartResponse> =>
    this.client.fetch<StoreAddSavedListToCartResponse>(
      `/store/saved-lists/${id}/add-to-cart`,
      { method: "POST", body: { dry_run: false, selections }, headers }
    );

  /**
   * Snapshot the active seller carts into a new list. Non-destructive; `name`
   * defaults to a date-based name server-side when omitted.
   */
  fromCart = async (
    name?: string,
    headers?: ClientHeaders
  ): Promise<StoreSavedListResponse> =>
    this.client.fetch<StoreSavedListResponse>("/store/saved-lists/save-cart", {
      method: "POST",
      body: (name
        ? { name }
        : {}) satisfies SalonStockerHttpTypes.StoreSaveCartAsList,
      headers
    });
}
